import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth, homeFor } from '../context/AuthContext.jsx';
import CubMascot from '../components/CubMascot.jsx';
import logoUrl from '../assets/logo.png';
import PublicShell from '../components/PublicShell.jsx';

/**
 * The public homepage.
 *
 * Before this, cybercub.org opened on a login form - which is fine for
 * a child who has been given an account and no use at all to the parent
 * or teacher deciding whether to get them one. This page is for that
 * second person: what the course is, what a child actually does in it,
 * what it costs, and where to sign up.
 *
 * It asks nothing of the server except the price, and it does not
 * redirect a signed-in visitor away. Somebody who is already a member
 * may still want to read the page they were sent; the bar simply
 * offers them their courses instead of a login form.
 */

/* Rupees from paise, without a trailing .00 nobody wants to read. */

/* The API returns the full titles, which all begin "CyberCub:". In a
   price list that prefix is three words of noise on every row. */
const HOW = [
  {
    icon: '📖',
    title: 'Read a little',
    text: 'Short pages with pictures, not walls of text. A lesson is ten to fifteen minutes.',
  },
  {
    icon: '🧪',
    title: 'Then do something',
    text: 'Practicals open in their own tab: take a computer apart, trace a message, diagnose a slow machine.',
  },
  {
    icon: '🎯',
    title: 'Prove it',
    text: 'A quiz at the end of every lesson. Get it wrong and it explains why, then lets you try again.',
  },
  {
    icon: '🏅',
    title: 'Collect the badge',
    text: 'Thirty badges across the courses. Progress is saved, so a lesson can be picked up next week.',
  },
];

const FOR_GROWNUPS = [
  {
    icon: '👨‍👩‍👧',
    title: 'Parents',
    text: 'Your child’s account is set up for them — then they just sign in and start. No adverts, nothing to install, and no chat with strangers anywhere in it.',
  },
  {
    icon: '🏫',
    title: 'Schools',
    text: 'Teachers get their own login and can see how each child in their class is getting on. Accounts are created for pupils rather than by them.',
  },
];

export default function Home() {
  const { user } = useAuth();
  /*
   * Arriving from another page at /#courses has to scroll there.
   *
   * Three things conspire against a single scrollIntoView. The router
   * changes the URL before this page has rendered, so the browser's
   * own anchor handling finds nothing. The price arrives afterwards and
   * moves the page under it. And a smooth scroll is an animation, which
   * that later change interrupts.
   *
   * So: instant rather than smooth, and keep correcting for a second
   * until the target has actually settled at the top.
   */
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return undefined;

    let ticks = 0;
    const id = setInterval(() => {
      const el = document.querySelector(hash);
      if (el) {
        const off = el.getBoundingClientRect().top;
        if (Math.abs(off) > 4) window.scrollBy({ top: off, behavior: 'auto' });
      }
      if (++ticks > 20) clearInterval(id);
    }, 50);

    return () => clearInterval(id);
  }, [hash]);

  useEffect(() => {
    document.title = 'CyberCub · Cyber safety for children';
  }, []);

  return (
    <PublicShell onHome>
      {/* ---------------- nav ---------------- */}
      {/* ---------------- hero ---------------- */}
      <section className="home-hero" id="top">
        <div className="home-hero-text">
          <span className="home-eyebrow">Cyber safety, built for children</span>
          <h1>
            Teach them to think
            <br />
            <span className="hl">before they click.</span>
          </h1>
          <p className="home-lede">
            Children meet the internet years before anybody explains it to them. CyberCub does the
            explaining — with short lessons, real practicals and badges worth having, for ages 7
            and up.
          </p>

          <div className="home-cta">
            {user ? (
              <Link to={homeFor(user.role)} className="btn btn-lg">Go to my courses →</Link>
            ) : (
              <Link to="/login" className="btn btn-lg">Log in →</Link>
            )}
          </div>

          <ul className="home-reassure">
            <li>✅ No adverts</li>
            <li>✅ No chat, ever</li>
            <li>✅ Nothing to install</li>
          </ul>
        </div>

        {/*
          * The emblem, given somewhere to sit.
          *
          * Dropping the badge in on its own would have read as a big
          * sticker: it is a dark disc, and the page around it is cream.
          * So it gets a cyan aura in its own accent colour, two slowly
          * turning rings that echo its rim - dashed and dotted, at
          * different speeds, so the pair never lines up and the whole
          * thing keeps moving without ever demanding attention - and
          * three badge chips out at the corners where the circle leaves
          * the square empty.
          *
          * The chips are the three things the course is actually about,
          * and they sit still. Emoji riding a rotating ring tumble
          * upside down, which is both hard to read and a strange thing
          * to put in front of a seven-year-old.
          */}
        <div className="home-hero-art" aria-hidden="true">
          <span className="home-blob home-blob-1" />
          <span className="home-blob home-blob-2" />

          <div className="hero-emblem">
            <span className="hero-aura" />
            <span className="hero-ring hero-ring-out" />
            <span className="hero-ring hero-ring-in" />
            <img
              src={logoUrl}
              alt=""
              width="250"
              height="250"
              className="hero-emblem-mark anim-float"
            />
            <span className="hero-chip hero-chip-1">🔐</span>
            <span className="hero-chip hero-chip-2">🎣</span>
            <span className="hero-chip hero-chip-3">🌐</span>
          </div>
        </div>
      </section>

      {/* ---------------- how it works ---------------- */}
      <section className="home-section home-band" id="how">
        <h2 className="home-h2">How a lesson goes</h2>
        <p className="home-sub">
          Nobody learns to stay safe online by being told to be careful. Every lesson ends with the
          child having done something.
        </p>

        <div className="home-how">
          {HOW.map((h, i) => (
            <div key={h.title} className="home-how-step">
              <span className="home-how-n" aria-hidden="true">{i + 1}</span>
              <span className="home-how-icon" aria-hidden="true">{h.icon}</span>
              <strong>{h.title}</strong>
              <p>{h.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ---------------- parents and schools ---------------- */}
      <section className="home-section" id="grownups">
        <h2 className="home-h2">For the grown-ups</h2>
        <div className="home-grownups">
          {FOR_GROWNUPS.map((g) => (
            <div key={g.title} className="home-grownup">
              <span className="home-grownup-icon" aria-hidden="true">{g.icon}</span>
              <div>
                <strong>{g.title}</strong>
                <p>{g.text}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="home-note">
          <strong>What we do not do.</strong> No advertising, no selling anything to your child, and
          no messaging between users — there is nobody for a stranger to be. Accounts hold a name, a
          year group and a parent's mobile number, and that is all.
        </div>
      </section>

      {/* ---------------- footer ---------------- */}
    </PublicShell>
  );
}
